import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  GitMerge, User, Calendar, MapPin, Building2, Loader2,
  ExternalLink, CheckCircle2, AlertTriangle,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDate } from "../lib/dateFormat";

/**
 * Feb 2026 · User-Directive „Ambiguous-Migration-Assistent".
 *
 * Admin-only Seite zur manuellen Zuordnung von orphan
 * `request_anything`-Anfragen zu Buchungen, bei denen das Batch-Migration-
 * Skript wegen ≥ 2 passender Buchungen konservativ „ambiguous" gemeldet
 * hat. Für jede Anfrage werden die möglichen Buchungs-Kandidaten als
 * Karten angezeigt; ein Klick auf „Diese Buchung wählen" ruft
 * `POST /api/team/migration-review/{req_id}/link` mit der booking_id auf.
 *
 * Datenquelle: `GET /api/team/migration-review/candidates` — dieselbe
 * Logik wie im CLI-Skript, nur reversibler und mit Rich-Kontext (Objekt-
 * Namen, Buchungs-Status, Kunden-Name).
 */
export default function TeamMigrationReview() {
  const [rows, setRows] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/team/migration-review/candidates");
      setRows(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
      setRows([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const linkTo = async (req_id, booking_id) => {
    setSavingId(req_id);
    try {
      await api.post(`/team/migration-review/${req_id}/link`, { booking_id });
      toast.success("Anfrage der Buchung zugewiesen.");
      // Reload — der zugewiesene Kandidat verschwindet aus der Liste.
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div
      className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14"
      data-testid="team-migration-review"
    >
      <header className="mb-8">
        <div className="label-caps mb-3">Weissblom · Admin</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">
          Migrations-Assistent
        </h1>
        <p className="text-[13.5px] text-navy-dark/70 mt-3 max-w-2xl leading-relaxed">
          Anfragen, deren Wunschdatum zu <strong>mehr als einer</strong>{" "}
          Buchung passt — das Batch-Skript hat sie konservativ liegen gelassen.
          Wähle je Anfrage die passende Buchung; die booking_id wird sofort
          gesetzt und der Ankunft-Latch der Zielbuchung greift ab dem
          nächsten Reload.
        </p>
      </header>

      {rows === null ? (
        <div className="flex items-center gap-2 text-[13px] text-navy-dark/50 italic py-6">
          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.6} />
          Kandidaten werden geladen …
        </div>
      ) : error ? (
        <div
          className="bg-white border border-red-500/30 p-6 text-[13px]"
          style={{ background: withAlpha(STATUS_COLORS.red, 0.04) }}
          data-testid="team-migration-review-error"
        >
          <div className="flex items-center gap-2" style={{ color: STATUS_COLORS.red }}>
            <AlertTriangle className="w-4 h-4" strokeWidth={1.8} />
            Kandidatenliste konnte nicht geladen werden — {error}
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div
          className="bg-white border border-navy/10 py-12 text-center"
          data-testid="team-migration-review-empty"
        >
          <div
            className="w-14 h-14 mx-auto flex items-center justify-center rounded-full mb-4"
            style={{ background: withAlpha(STATUS_COLORS.green, 0.1), color: STATUS_COLORS.green }}
          >
            <CheckCircle2 className="w-7 h-7" strokeWidth={1.6} />
          </div>
          <div className="font-display text-xl text-navy">
            Keine mehrdeutigen Kandidaten
          </div>
          <p className="text-[13px] text-navy-dark/60 mt-2 max-w-md mx-auto">
            Alle offenen orphan-Anfragen sind entweder bereits eindeutig
            zugeordnet oder haben kein Wunschdatum, das in ein Buchungs-Fenster
            fällt. Läuft die Batch-Migration erneut, wird auch die
            Assistent-Liste sofort neu befüllt.
          </p>
        </div>
      ) : (
        <>
          <div
            className="mb-6 text-[12.5px] text-navy-dark/60"
            data-testid="team-migration-review-count"
          >
            {rows.length} mehrdeutige{" "}
            {rows.length === 1 ? "Anfrage" : "Anfragen"} · älteste zuerst
          </div>
          <div className="space-y-5">
            {rows.map((r) => (
              <CandidateCard
                key={r.request.id}
                row={r}
                onLink={linkTo}
                saving={savingId === r.request.id}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CandidateCard({ row, onLink, saving }) {
  const { request: req, candidates } = row;
  return (
    <div
      className="bg-white border border-navy/15 overflow-hidden"
      data-testid={`migration-request-${req.id}`}
      data-count={candidates.length}
    >
      <div
        className="px-6 py-4 border-b border-navy/10 flex items-start justify-between gap-4 flex-wrap"
        style={{ background: withAlpha(BRAND_COLORS.navy, 0.02) }}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span
              className="label-caps text-[10px] tracking-[0.14em] px-2 py-0.5"
              style={{ background: withAlpha(BRAND_COLORS.gold, 0.15), color: BRAND_COLORS.goldDark }}
            >
              Welche Buchung passt?
            </span>
            <span className="label-caps text-[10px] text-navy-dark/50">
              Wunschdatum · {deDate(req.date_needed)}
            </span>
          </div>
          <h2 className="font-display text-2xl text-navy leading-tight">
            {req.title || "Anfrage"}
          </h2>
          {req.description && (
            <p className="text-[13px] text-navy-dark/70 mt-2 leading-relaxed max-w-3xl">
              {req.description}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-4 text-[12px] text-navy-dark/70">
            <span className="inline-flex items-center gap-1.5">
              <User size={12} style={{ color: BRAND_COLORS.gold }} />
              {req.customer_name}
            </span>
            <Link
              to={`/team/customers/${req.customer_id}`}
              className="inline-flex items-center gap-1 text-navy-dark/60 border-b border-transparent hover:border-gold hover:text-navy transition-colors"
              data-testid={`migration-request-${req.id}-open-customer`}
            >
              Kundenprofil
              <ExternalLink size={11} strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-3">
        {candidates.map((c) => (
          <BookingCandidateCard
            key={c.id}
            candidate={c}
            onPick={() => onLink(req.id, c.id)}
            disabled={saving}
            reqId={req.id}
          />
        ))}
      </div>
    </div>
  );
}

const BOOKING_STATUS_LABEL = {
  requested: "Angefragt",
  confirmed: "Bestätigt",
  in_progress: "In Betreuung",
  completed: "Abgeschlossen",
  cancelled: "Storniert",
};

function BookingCandidateCard({ candidate, onPick, disabled, reqId }) {
  const label = BOOKING_STATUS_LABEL[candidate.status] || candidate.status;
  return (
    <div
      className="border border-navy/10 hover:border-gold transition-colors bg-white"
      data-testid={`migration-request-${reqId}-candidate-${candidate.id}`}
    >
      <div className="px-4 py-3 border-b border-navy/5 flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[12.5px] text-navy font-medium">
            <Calendar size={13} strokeWidth={1.7} style={{ color: BRAND_COLORS.gold }} />
            {deDate(candidate.from_date)} → {deDate(candidate.to_date)}
          </div>
          <div className="text-[11px] text-navy-dark/50 mt-1 label-caps tracking-[0.12em]">
            {label}
          </div>
        </div>
      </div>
      <div className="px-4 py-3 space-y-2">
        <div className="text-[12.5px] text-navy inline-flex items-center gap-1.5 min-w-0">
          <Building2 size={12} strokeWidth={1.7} style={{ color: BRAND_COLORS.gold }} />
          <span className="truncate">{candidate.property_name || "Objekt ohne Namen"}</span>
        </div>
        {(candidate.property_address || candidate.property_city) && (
          <div className="text-[11.5px] text-navy-dark/60 inline-flex items-start gap-1.5">
            <MapPin size={11} strokeWidth={1.7} className="mt-0.5 shrink-0" style={{ color: BRAND_COLORS.gold }} />
            <span className="truncate">
              {candidate.property_address}
              {candidate.property_city ? `, ${candidate.property_city}` : ""}
            </span>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onPick}
        disabled={disabled}
        data-testid={`migration-pick-${reqId}-${candidate.id}`}
        className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 text-[11px] tracking-[0.15em] uppercase text-ivory hover:opacity-90 transition-opacity disabled:opacity-40"
        style={{ background: BRAND_COLORS.navy }}
      >
        {disabled ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />
        ) : (
          <GitMerge className="w-3.5 h-3.5" strokeWidth={1.8} />
        )}
        Diese Buchung wählen
      </button>
    </div>
  );
}
